import React, { useState, useMemo, memo, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Table, Braces, FileText } from "lucide-react";
import { List } from "react-window";
import { URL_RE, LOG_COLORS, toRows, extractColumns, formatCell, formatLogArg } from "./executionUtils";

const VirtualRow = memo(function VirtualRow({ index, style, data }) {
    const { rows, columns, expandedIdx, onToggle } = data;
    const row = rows[index];
    const isExpanded = expandedIdx === index;
    const handleClick = () => { if (!window.getSelection()?.toString()) onToggle(index); };
    return (
        <div style={style} onClick={handleClick}
            className={`flex border-b border-gray-100 cursor-pointer hover:bg-blue-50 ${index % 2 ? "bg-gray-50" : "bg-white"} ${isExpanded ? "!bg-blue-50" : ""}`}>
            {columns.map((col) => (
                <div key={col} className={`px-3 py-1.5 text-gray-700 text-[12px] min-w-[150px] flex-1 ${isExpanded ? "break-words whitespace-pre-wrap" : "truncate"}`}>
                    {row[col] != null && typeof row[col] === "string" && URL_RE.test(row[col].trim())
                        ? <a href={row[col].trim()} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 underline hover:text-blue-800">{isExpanded ? row[col] : row[col].slice(0, 60) + "\u2026"}</a>
                        : formatCell(row[col])}
                </div>
            ))}
        </div>
    );
});

function FullTable({ rows, columns }) {
    const [expandedIdx, setExpandedIdx] = useState(null);
    const toggle = useCallback((i) => setExpandedIdx((p) => p === i ? null : i), []);
    const itemData = useMemo(() => ({ rows, columns, expandedIdx, onToggle: toggle }), [rows, columns, expandedIdx, toggle]);
    return (
        <div className="border border-gray-200 rounded overflow-hidden flex flex-col h-full">
            <div className="flex bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                {columns.map((col) => <div key={col} className="px-3 py-2 text-left font-medium text-gray-500 text-xs min-w-[150px] flex-1">{col}</div>)}
            </div>
            <div className="flex-1 overflow-auto">
                {rows.map((row, idx) => <VirtualRow key={row.id || row._id || idx} index={idx} style={{}} data={itemData} />)}
            </div>
        </div>
    );
}

function JsonLine({ index, style, lines }) {
    return (
        <div style={style} className="flex text-xs font-mono">
            <span className="w-12 text-gray-400 text-right pr-3 select-none flex-shrink-0">{index + 1}</span>
            <span className="text-gray-700 whitespace-pre">{lines[index]}</span>
        </div>
    );
}

function FullJsonView({ result }) {
    const lines = useMemo(() => JSON.stringify(result, null, 2).split("\n"), [result]);
    return (
        <div className="bg-gray-50 rounded border border-gray-200 flex-1 overflow-hidden">
            <List height={600} rowCount={lines.length} rowHeight={20} rowComponent={JsonLine} rowProps={{ lines }} />
        </div>
    );
}

function FullLogsSection({ logs }) {
    const entries = useMemo(() => {
        if (!logs) return null;
        if (Array.isArray(logs)) return logs;
        if (typeof logs === "string") return [{ type: "log", args: [logs] }];
        return null;
    }, [logs]);
    if (!entries?.length) return null;
    return (
        <div className="border border-gray-200 rounded overflow-hidden flex-1">
            <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                        <th className="text-left px-3 py-2 font-medium text-gray-500 w-[60px]">Type</th>
                        <th className="text-left px-3 py-2 font-medium text-gray-500">Message</th>
                    </tr>
                </thead>
                <tbody>
                    {entries.filter(Boolean).map((entry, idx) => (
                        <tr key={idx} className={`border-b border-gray-100 ${idx % 2 ? "bg-gray-50" : "bg-white"}`}>
                            <td className={`px-3 py-2 font-mono ${LOG_COLORS[entry.type] || LOG_COLORS.log}`}>{entry.type || "log"}</td>
                            <td className="px-3 py-2 text-gray-700 whitespace-pre-wrap break-words">
                                {Array.isArray(entry.args) ? entry.args.map(formatLogArg).join(" ") : formatLogArg(entry)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const TAB = "px-3 py-1 text-xs font-medium rounded flex items-center gap-1.5 transition-colors";

export default function ExecutionResultModal({ selected, logs, onClose }) {
    const [view, setView] = useState("table");
    const rows = useMemo(() => selected ? toRows(selected.result) : null, [selected?.result]);
    const columns = useMemo(() => rows ? extractColumns(rows) : null, [rows]);
    const itemCount = rows?.length ?? null;
    const stepName = selected?.step?.name || selected?.step?.type || "Step";

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-[90vw] h-[85vh] max-w-[1400px] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-800">{stepName}</span>
                        {selected?.result && (
                            <div className="flex items-center gap-0.5 bg-gray-100 rounded p-0.5">
                                <button onClick={() => setView("table")} className={`${TAB} ${view === "table" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`}>
                                    <Table size={12} /> Table
                                </button>
                                <button onClick={() => setView("json")} className={`${TAB} ${view === "json" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`}>
                                    <Braces size={12} /> JSON
                                </button>
                                {logs && (
                                    <button onClick={() => setView("logs")} className={`${TAB} ${view === "logs" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`}>
                                        <FileText size={12} /> Logs
                                    </button>
                                )}
                            </div>
                        )}
                        {itemCount != null && <span className="text-xs text-gray-400">{itemCount} item{itemCount !== 1 ? "s" : ""}</span>}
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X size={16} className="text-gray-400" /></button>
                </div>
                <div className="flex-1 overflow-auto p-5">
                    {view === "logs" ? (
                        <FullLogsSection logs={logs} />
                    ) : selected?.result ? (
                        view === "table" && rows ? <FullTable rows={rows} columns={columns} /> : <FullJsonView result={selected.result} />
                    ) : (
                        <div className="text-sm text-gray-400 italic">No output</div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
